import { API_BASE_URL } from '@synergy/config';
import { AutocompleteOption, PoolFormikAutocomplete, PoolFormikAutocompleteProps } from '@synergy/components-next';
import { FormattedMessage, useIntl } from 'react-intl';
import { FunctionalAsset } from '@synergy/models';
import { ReactNode, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { SupplierApi } from '../../../../api/supplier-api';

export interface FunctionalAssetAutocompleteProps
    extends Omit<
        Partial<PoolFormikAutocompleteProps>,
        'options' | 'renderValue' | 'classes' | 'onValueChange' | 'selectedLabel'
    > {
    supplierId?: number;
    showLabel?: boolean;
    label?: ReactNode;
    useId?: boolean;
    onValueChange?: (functionalAsset: FunctionalAsset | null) => void;
}

export interface FunctionalAssetAutocompleteRef {
    getFunctionalAsset: (id?: number | null) => FunctionalAsset | null;
}

export const FunctionalAssetAutocomplete = forwardRef<FunctionalAssetAutocompleteRef, FunctionalAssetAutocompleteProps>(
    (props, ref) => {
        const {
            supplierId,
            required = true,
            showLabel = true,
            label,
            name = 'functionalAsset',
            useId = false,
            onValueChange,
            ...restProps
        } = props;

        const api = useRef(new SupplierApi(API_BASE_URL)).current;
        const [functionalAssets, setFunctionalAssets] = useState<FunctionalAsset[] | null>(null);
        const intl = useIntl();

        useImperativeHandle(ref, () => ({
            getFunctionalAsset: (id?: number | null) =>
                functionalAssets?.find(functionalAsset => functionalAsset.id === id) ?? null,
        }));

        useEffect(() => {
            const asyncEffect = async (): Promise<void> => {
                if (supplierId) {
                    const response = await api.getFunctionalAssets(supplierId);
                    setFunctionalAssets(response.success && response.result.length > 0 ? response.result : null);
                }
            };
            asyncEffect();
        }, [supplierId, api]);

        const getDisplayValue = (value: FunctionalAsset | number | null): string => {
            if (typeof value === 'number') {
                return `${value}`;
            }

            return value
                ? [value.objectId, value.objectDescription]
                      .filter(part => part !== null && part !== undefined && part !== '')
                      .join(' : ')
                : '';
        };

        const options: AutocompleteOption<FunctionalAsset | number>[] = useMemo(() => {
            return (
                functionalAssets?.map(functionalAsset => ({
                    value: useId ? functionalAsset.id : functionalAsset,
                    displayValue: getDisplayValue(functionalAsset),
                })) ?? []
            );
        }, [functionalAssets, useId]);

        const handleSelected = (value: FunctionalAsset | number | null): void => {
            if (!value) {
                return;
            }

            if (typeof value === 'number') {
                const functionalAsset = functionalAssets?.find(functionalAsset => functionalAsset.id === value);
                onValueChange?.(functionalAsset ?? null);
            } else {
                onValueChange?.(value);
            }
        };

        const getValueFunction = (value: FunctionalAsset | number | null): FunctionalAsset | number | null => {
            if (!value) {
                return null;
            }

            if (typeof value === 'number') {
                return value;
            } else {
                return functionalAssets?.find(functionalAsset => functionalAsset.id === value.id) ?? null;
            }
        };

        let autocompleteLabel = showLabel ? label : undefined;
        autocompleteLabel ??= <FormattedMessage id="app.label.functionalAsset" />;

        return (
            <PoolFormikAutocomplete<FunctionalAsset | number>
                placeholder={intl.formatMessage({ id: 'app.label.functionalAsset' })}
                label={autocompleteLabel}
                options={options}
                fullWidth={true}
                required={required}
                name={name}
                {...restProps}
                onChange={handleSelected}
                getValue={getValueFunction}
                getDisplayValue={getDisplayValue}
            />
        );
    }
);
